import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
  isDisabled$,
} from '@principle-theorem/ng-shared';
import {
  Invoice,
  Transaction,
  TransactionOperators,
  isDiscountTransaction,
  type IPractitionerProportionInvoiceAmount,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IInvoice,
  type IPractice,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  isSameRef,
  shareReplayCold,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { omit, sortBy } from 'lodash';
import { Subject, type Observable } from 'rxjs';
import { map, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  TransactionPracticeOptionMethods,
  TransactionPracticeOptions,
} from '../transaction-components/transaction-practice-options';

export interface IDiscountTransactionData {
  invoice: WithRef<IInvoice>;
  to: string;
  from: string;
  amount: number;
}

interface IDiscountFormData extends Pick<IDiscountTransactionData, 'amount'> {
  description: string;
  practiceRef: DocumentReference<IPractice>;
  attributedTo?: DocumentReference<IStaffer>;
}

@Component({
    selector: 'pr-discount-transaction',
    templateUrl: './discount-transaction.component.html',
    styleUrls: ['./discount-transaction.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiscountTransactionComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  form: TypedFormGroup<IDiscountFormData>;
  isDisabled$: Observable<boolean>;
  maxAmount$: Observable<number>;
  invoicePractitioners: IPractitionerProportionInvoiceAmount[];
  practiceOptions: TransactionPracticeOptions;
  staff: INamedDocument<IStaffer>[];
  showAttributedTo: boolean;

  constructor(
    private _dialogRef: MatDialogRef<
      DiscountTransactionComponent,
      ITransaction
    >,
    @Inject(MAT_DIALOG_DATA) public data: IDiscountTransactionData,
    private _currentScope: CurrentScopeFacade
  ) {
    this.form = new TypedFormGroup<IDiscountFormData>({
      amount: new TypedFormControl<number>(this.data.amount, [
        Validators.required,
        Validators.max(this.data.amount),
        Validators.min(0),
      ]),
      description: new TypedFormControl<string>(''),
      practiceRef: new TypedFormControl<DocumentReference<IPractice>>(
        undefined,
        Validators.required
      ),
      attributedTo: new TypedFormControl<
        DocumentReference<IStaffer> | undefined
      >(),
    });

    this.isDisabled$ = isDisabled$(this.form);
    this.form.patchValue(this.data);
    this.practiceOptions = new TransactionPracticeOptions(
      this._currentScope.currentPractice$,
      TransactionPracticeOptionMethods.toInvoicePracticeOption(
        this.data.invoice
      ),
      true
    );
    this.practiceOptions.initialValue$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );

    this.invoicePractitioners = sortBy(
      Invoice.getPractitionerProportionsOnInvoice(data.invoice),
      (practitioner) => practitioner.practitioner.name
    );
    this.staff = Invoice.staffOnInvoice(data.invoice);
    this.showAttributedTo =
      this.staff.length > 1 ||
      TransactionAllocation.hasUnallocatedAmount(this.data.invoice);

    const activeDiscounts$ = Invoice.transactions$(data.invoice).pipe(
      map((transactions) =>
        new TransactionOperators(transactions)
          .byProvider(TransactionProvider.Discount)
          .transactionGuard(isDiscountTransaction)
          .result()
      )
    );

    this.maxAmount$ = formControlChanges$(this.form.controls.attributedTo).pipe(
      withLatestFrom(activeDiscounts$),
      map(([selectedPractitionerRef, activeDiscounts]) => {
        const existingPractitionerDiscountAmount = new TransactionOperators(
          activeDiscounts.filter((discount) => {
            if (!discount.attributedTo) {
              return true;
            }

            if (isSameRef(discount.attributedTo, selectedPractitionerRef)) {
              return true;
            }

            return false;
          })
        ).paidToDate();

        if (!selectedPractitionerRef) {
          return this.data.amount;
        }

        const practitionerProportion = this.invoicePractitioners.find(
          (practitioner) =>
            isSameRef(practitioner.practitioner, selectedPractitionerRef)
        );

        return practitionerProportion
          ? practitionerProportion.amount - existingPractitionerDiscountAmount
          : this.data.amount;
      }),
      map((maxValue) =>
        this.data.amount < maxValue ? this.data.amount : maxValue
      ),
      tap((maxValue) => {
        const currentValue = this.form.controls.amount.value ?? undefined;
        if (currentValue && currentValue > maxValue) {
          this.form.controls.amount.setValue(maxValue);
        }
        this.form.controls.amount.setValidators([
          Validators.required,
          Validators.max(maxValue),
          Validators.min(0),
        ]);
        this.form.updateValueAndValidity();
      }),
      startWith(this.data.amount),
      shareReplayCold()
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }
    const data = this.form.getRawValue();
    const transaction = Transaction.init({
      ...omit(this.data, 'invoice'),
      amount: data.amount,
      ...Transaction.internalReference(TransactionProvider.Discount),
      type: TransactionType.Incoming,
      status: TransactionStatus.Complete,
      description: data.description,
      practiceRef: data.practiceRef,
      extendedData: {
        practitionerRef: data.attributedTo,
      },
      attributedTo: data.attributedTo,
    });
    this._dialogRef.close(transaction);
  }
}
